import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus } from 'lucide-react';
import StuiFrame from './StuiFrame';
import { parseSearchQuery, buildSearchQuery, splitValueList } from '../../utils/listSearch';
import StuiSelect from './StuiSelect';

/**
 * Visual builder for the list-search syntax (utils/listSearch.js).
 *
 * The page's search box stays the single source of truth: the builder opens by parsing whatever
 * is in the box, and Apply writes a query string back. So everything the builder makes can also be
 * typed, and the live preview teaches the syntax by showing what each row turns into.
 *
 * Model: OR-groups of rows, each row { field, op, value }. field '' = any field. A specific field's
 * value is a comma list (any of); quote an item to keep a comma literal. An any-field value is
 * always one literal.
 *
 * fields: [{ key, label }] — every field the page searches, labelled as its column reads.
 */
const OPS = [
  { value: 'contains', label: 'contains' },
  { value: 'not',      label: 'does not contain' },
];

const emptyRow = () => ({ field: '', op: 'contains', value: '' });
const emptyGroup = () => ({ rows: [emptyRow()] });

const showValues = (values) => values.map(v => (v.includes(',') ? `"${v}"` : v)).join(', ');

const toModel = (query, fields) => {
  const { groups } = parseSearchQuery(query, fields.map(f => f.key), fields);
  const model = groups
    .filter(g => g.clauses.length)
    .map(g => ({
      rows: g.clauses.map(c => ({
        field: c.field || '',
        op: c.negated ? 'not' : 'contains',
        value: c.field ? showValues(c.values) : c.values[0],
      })),
    }));
  return model.length ? model : [emptyGroup()];
};

const toGroups = (model) => model.map(g => ({
  clauses: g.rows
    .filter(r => r.value.trim())
    .map(r => ({
      field: r.field || null,
      values: r.field ? splitValueList(r.value) : [r.value.replace(/"/g, '').trim()],
      negated: r.op === 'not',
    })),
}));

const AdvancedSearchModal = ({ isOpen, onClose, query, fields, onApply }) => {
  const [model, setModel] = useState([emptyGroup()]);

  useEffect(() => {
    if (isOpen) setModel(toModel(query || '', fields || []));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const preview = useMemo(() => buildSearchQuery(toGroups(model), fields || []), [model, fields]);

  if (!isOpen) return null;

  const setRow = (gi, ri, patch) => setModel(m => m.map((g, i) => (i !== gi ? g : {
    rows: g.rows.map((r, j) => (j === ri ? { ...r, ...patch } : r)),
  })));
  const addRow = (gi) => setModel(m => m.map((g, i) => (i === gi ? { rows: [...g.rows, emptyRow()] } : g)));
  const removeRow = (gi, ri) => setModel(m => {
    const rows = m[gi].rows.filter((_, j) => j !== ri);
    if (rows.length) return m.map((g, i) => (i === gi ? { rows } : g));
    const rest = m.filter((_, i) => i !== gi);
    return rest.length ? rest : [emptyGroup()];
  });
  const addGroup = () => setModel(m => [...m, emptyGroup()]);

  const apply = () => { onApply?.(preview); onClose?.(); };
  const onValueKey = e => { if (e.key === 'Enter') { e.preventDefault(); apply(); } };

  return (
    <div className="stui-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="stui-modal stui-modal-lg">
        <div className="stui-modal-header">
          <h3>Advanced Search</h3>
          <button className="stui-modal-close" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body">
          <p className="stui-modal-hint">
            A record matches when it satisfies every condition in at least one box.
            For a specific field, separate values with commas to match any of them.
          </p>

          {model.map((g, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && <div className="stui-search-builder-or">OR</div>}
              <StuiFrame caption="Match ALL of these">
                {g.rows.map((r, ri) => (
                  <div key={ri} className="stui-search-builder-row">
                    <StuiSelect className="stui-field-input" value={r.field} onChange={e => setRow(gi, ri, { field: e.target.value })} aria-label="Field">
                      <option value="">Any field</option>
                      {(fields || []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </StuiSelect>
                    <StuiSelect className="stui-field-input" value={r.op} onChange={e => setRow(gi, ri, { op: e.target.value })} aria-label="Operator">
                      {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </StuiSelect>
                    <input
                      className="stui-field-input"
                      value={r.value}
                      onChange={e => setRow(gi, ri, { value: e.target.value })}
                      onKeyDown={onValueKey}
                      placeholder={r.field ? 'value, or a, b for any of' : 'text'}
                      aria-label="Value"
                      autoFocus={gi === 0 && ri === 0}
                    />
                    <button type="button" className="stui-crud-btn" onClick={() => removeRow(gi, ri)} title="Remove condition"><X size={16} /></button>
                  </div>
                ))}
                <button type="button" className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => addRow(gi)}>
                  <Plus size={14} /> Add condition
                </button>
              </StuiFrame>
            </React.Fragment>
          ))}

          <div className="stui-btn-row">
            <button type="button" className="stui-btn stui-btn-secondary stui-btn-sm" onClick={addGroup}>
              <Plus size={14} /> Add OR group
            </button>
          </div>

          <div className="stui-form-row">
            <label>Search text (what the search box will contain)</label>
            <input className="stui-field-input stui-field-readonly" value={preview} readOnly placeholder="(empty — clears the search)" />
          </div>
        </div>

        <div className="stui-modal-footer">
          <button type="button" className="stui-btn stui-btn-secondary" onClick={() => setModel([emptyGroup()])}>Clear</button>
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-primary" onClick={apply}>Apply</button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchModal;
